import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProgereBrand } from "@/components/ProgereBrand";
import { Wrench, RefreshCw, Send, Lock, X } from "lucide-react";

const STATES = [
  ["nouveau", "Nouveau"], ["assigne", "Assigné"], ["en_analyse", "En analyse"],
  ["information_requise", "Information requise"], ["en_attente_client", "En attente du client"],
  ["en_attente_interne", "En attente interne"], ["en_correction", "En correction"],
  ["en_test", "En test"], ["resolu", "Résolu"], ["ferme", "Fermé"], ["rouvert", "Rouvert"],
];
const STATE_LABEL = Object.fromEntries(STATES);
const card = "rounded-xl border border-white/10 bg-[#111217] p-5";
const inputCls = "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-[#EAB308] focus:outline-none";

export default function TechnicianCenterPage() {
  const { user } = useAuth();
  const [ov, setOv] = useState(null);
  const [scope, setScope] = useState("all");
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [msg, setMsg] = useState("");
  const [note, setNote] = useState("");

  const loadOverview = useCallback(() => {
    api.get("/support/v2/tech/overview").then(({ data }) => setOv(data)).catch(() => {});
  }, []);
  const loadTickets = useCallback(() => {
    api.get(`/support/v2/tickets?scope=${scope}`).then(({ data }) => setTickets(data)).catch(() => {});
  }, [scope]);
  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openTicket = async (id) => {
    setSelected(id);
    try { const { data } = await api.get(`/support/v2/tickets/${id}`); setDetail(data); }
    catch (e) { toast.error("Erreur de chargement"); }
  };

  if (user && !user.is_platform_technician) return <Navigate to="/app" replace />;

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try { await api.post(`/support/v2/tickets/${selected}/messages`, { message: msg }); setMsg(""); openTicket(selected); }
    catch (e) { toast.error("Erreur"); }
  };
  const sendNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/support/v2/tickets/${selected}/notes`, { message: note }); setNote(""); openTicket(selected); }
    catch (e) { toast.error("Erreur"); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/support/v2/tickets/${selected}/status`, { status }); toast.success("Statut mis à jour"); openTicket(selected); loadTickets(); loadOverview(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
  };

  const Stat = ({ label, value, accent }) => (
    <div className={card}><div className="text-xs uppercase tracking-wider text-white/45">{label}</div>
      <div className={`mt-1 text-3xl font-black ${accent ? "text-[#EAB308]" : "text-white"}`}>{value ?? "—"}</div></div>
  );

  return (
    <div className="space-y-6" data-testid="technician-center-page">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#EAB308]/40 bg-[#EAB308]/10"><Wrench className="w-5 h-5 text-[#EAB308]" /></div>
        <div>
          <h1 className="text-2xl font-black"><ProgereBrand module="TECHNICIAN CENTER" size="medium" /></h1>
          <p className="text-sm text-white/50">Traitement des billets & diagnostics</p>
        </div>
        <button onClick={() => { loadOverview(); loadTickets(); }} className="ml-auto flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-sm text-white/60 hover:text-white"><RefreshCw className="w-4 h-4" /> Actualiser</button>
      </div>

      {ov && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Stat label="Ouverts" value={ov.open_total} accent />
          <Stat label="Mes billets" value={ov.assigned} />
          <Stat label="Non assignés" value={ov.unassigned} />
          <Stat label="Urgents" value={ov.urgent} />
          <Stat label="Attente client" value={ov.waiting_client} />
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <div className={card}>
          <div className="mb-3 flex items-center gap-2">
            <h2 className="text-lg font-bold text-white">Billets</h2>
            <select value={scope} onChange={(e) => setScope(e.target.value)} className={inputCls + " ml-auto max-w-[160px]"} data-testid="tech-scope">
              <option value="all">Tous</option>
              <option value="mine">Mes billets</option>
              <option value="unassigned">Non assignés</option>
            </select>
          </div>
          <div className="space-y-2 max-h-[540px] overflow-y-auto">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => openTicket(t.id)} data-testid={`tech-ticket-${t.ref}`}
                className={`w-full text-left rounded-lg border px-4 py-2.5 transition-colors ${selected === t.id ? "border-[#EAB308]/50 bg-[#EAB308]/5" : "border-white/8 bg-black/30 hover:border-white/20"}`}>
                <div className="text-sm font-semibold text-white">{t.subject}</div>
                <div className="text-xs font-mono text-[#EAB308]">{t.ref} · {t.module_code} · {t.priority}</div>
                <div className="mt-1 text-[11px] text-white/50">{STATE_LABEL[t.status] || t.status}</div>
              </button>
            ))}
            {tickets.length === 0 && <p className="text-sm text-white/40">Aucun billet.</p>}
          </div>
        </div>

        <div className={card}>
          {!detail ? (
            <p className="text-sm text-white/40">Sélectionnez un billet pour voir les détails.</p>
          ) : (
            <div className="space-y-4" data-testid="ticket-detail">
              <div>
                <div className="text-xs font-mono text-[#EAB308]">{detail.ref}</div>
                <div className="text-lg font-bold text-white">{detail.subject}</div>
                <div className="text-xs text-white/50">{detail.module_code} · {detail.category} · {detail.priority}</div>
              </div>
              <p className="rounded-lg bg-black/40 p-3 text-sm text-white/70">{detail.description}</p>

              {detail.diagnostics?.correlation_id && (
                <div className="rounded-lg border border-white/8 bg-black/30 p-3 text-xs text-white/50 font-mono">
                  <div className="mb-1 text-white/70">Diagnostic</div>
                  page {detail.diagnostics.page} · {detail.diagnostics.module_version || "?"} · corr {detail.diagnostics.correlation_id}
                </div>
              )}

              <div>
                <div className="mb-1 text-xs text-white/50">Statut</div>
                <select value={detail.status} onChange={(e) => setStatus(e.target.value)} className={inputCls} data-testid="ticket-status-select">
                  {STATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>

              <div>
                <div className="mb-1 text-xs text-white/50">Conversation</div>
                <div className="space-y-2 max-h-[180px] overflow-y-auto">
                  {(detail.messages || []).map((m) => (
                    <div key={m.id} className={`rounded-lg px-3 py-2 text-sm ${m.author_type === "technician" ? "bg-[#EAB308]/10 text-white" : "bg-white/5 text-white/80"}`}>
                      <span className="text-[10px] uppercase text-white/40">{m.author_type === "technician" ? "Technicien" : "Client"} · {m.author_name}</span>
                      <div>{m.message}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <input value={msg} onChange={(e) => setMsg(e.target.value)} className={inputCls} placeholder="Répondre au client…" data-testid="reply-input" />
                  <button onClick={sendMsg} className="shrink-0 rounded-lg bg-[#EAB308] px-3 text-black" data-testid="reply-send"><Send className="w-4 h-4" /></button>
                </div>
              </div>

              <div>
                <div className="mb-1 flex items-center gap-1.5 text-xs text-red-300"><Lock className="w-3.5 h-3.5" /> Notes internes (invisibles au client)</div>
                <div className="space-y-2 max-h-[120px] overflow-y-auto">
                  {(detail.internal_notes || []).map((n) => (
                    <div key={n.id} className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-sm text-white/80">
                      <span className="text-[10px] uppercase text-red-300/70">{n.author_name}</span>
                      <div>{n.message}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="Note interne…" data-testid="note-input" />
                  <button onClick={sendNote} className="shrink-0 rounded-lg border border-red-500/30 px-3 text-red-300" data-testid="note-send"><Send className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
